/**
 * Rider Payouts Page
 * Sprint 4G: Self-service earnings tracking
 * 
 * Shows:
 * - Pending earnings (not yet batched)
 * - Payment history (all batches)
 * - Delivery-based earnings breakdown
 */

import { useState, useEffect } from 'react';
import { riderApi } from '../lib/api';

interface PayoutBatch {
  id: string;
  rider_id: string;
  period_start: string;
  period_end: string;
  total_payout_cents: number;
  total_orders: number;
  status: 'pending' | 'processing' | 'paid' | 'failed';
  method: string | null;
  reference_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export default function PayoutsPage() {
  const [batches, setBatches] = useState<PayoutBatch[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // expandedBatchId state removed - not currently used

  useEffect(() => {
    loadBatches();
  }, []);

  const loadBatches = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await riderApi.getPayoutBatches();
      
      if (response.success && response.data) {
        setBatches(response.data.batches || []);
      } else {
        setError('Failed to load earnings');
      }
    } catch (err: any) {
      setError('Failed to load earnings');
      console.error('[PayoutsPage] Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'paid': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'processing': return '🔄';
      case 'paid': return '✅';
      case 'failed': return '❌';
      default: return '📦';
    }
  };

  // Calculate summary
  const totalPaid = batches
    .filter(b => b.status === 'paid')
    .reduce((sum, b) => sum + b.total_payout_cents, 0);
  
  const totalPending = batches
    .filter(b => b.status === 'pending' || b.status === 'processing')
    .reduce((sum, b) => sum + b.total_payout_cents, 0);

  const totalDeliveries = batches.reduce((sum, b) => sum + b.total_orders, 0);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading earnings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">🏍️ My Earnings</h1>
          <p className="text-gray-600">Track your delivery earnings and payouts</p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
            {error}
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Pending */}
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-yellow-800">Pending</h3>
              <span className="text-3xl">⏳</span>
            </div>
            <div className="text-3xl font-bold text-yellow-700">
              ₱{(totalPending / 100).toFixed(2)}
            </div>
            <p className="text-xs text-yellow-600 mt-1">
              Being processed
            </p>
          </div>

          {/* Paid */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-green-800">Total Paid</h3>
              <span className="text-3xl">✅</span>
            </div>
            <div className="text-3xl font-bold text-green-700">
              ₱{(totalPaid / 100).toFixed(2)}
            </div>
            <p className="text-xs text-green-600 mt-1">
              Received
            </p>
          </div>

          {/* Total Deliveries */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-blue-800">Deliveries</h3>
              <span className="text-3xl">🚚</span>
            </div>
            <div className="text-3xl font-bold text-blue-700">
              {totalDeliveries}
            </div>
            <p className="text-xs text-blue-600 mt-1">
              Completed orders
            </p>
          </div>
        </div>

        {/* Payout History */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-xl font-semibold text-gray-900">Payment History</h2>
          </div>

          {batches.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <div className="text-4xl mb-4">🏍️</div>
              <p className="text-lg font-medium mb-2">No earnings yet</p>
              <p className="text-sm">Complete deliveries to start earning!</p>
            </div>
          ) : (
            <div className="divide-y">
              {batches.map((batch) => (
                <div key={batch.id} className="p-6 hover:bg-gray-50 transition">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl">{getStatusIcon(batch.status)}</span>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">
                            ₱{(batch.total_payout_cents / 100).toFixed(2)}
                          </h3>
                          <span className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(batch.status)}`}>
                            {batch.status.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      
                      <div className="ml-11 space-y-1 text-sm text-gray-600">
                        <div>
                          📅 {new Date(batch.period_start).toLocaleDateString()} - {new Date(batch.period_end).toLocaleDateString()}
                        </div>
                        <div>
                          🚚 {batch.total_orders} deliveries
                        </div>
                        {batch.method && (
                          <div>
                            💳 Method: {batch.method.toUpperCase()}
                          </div>
                        )}
                        {batch.reference_id && (
                          <div>
                            🔖 Reference: <span className="font-mono text-xs">{batch.reference_id}</span>
                          </div>
                        )}
                        {batch.notes && (
                          <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                            📝 {batch.notes}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="text-right text-sm text-gray-500">
                      {new Date(batch.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

